import { motionOn } from './motion'

/**
 * Same-document View Transitions between routes (PAGES.md 1.3 rule 7).
 *
 * The app uses `<BrowserRouter>`, a declarative router: React Router 7.18's `<Link viewTransition>` is only honoured
 * by data routers, so it does nothing here (checked in `react-router` `BrowserRouter`, which ignores the option). `R5Link`
 * therefore starts the transition itself. The router commits a navigation inside `startTransition`, so a plain
 * `flushSync(navigate)` would not paint the new page inside the transition callback. Instead the callback returns a
 * promise that `R5Shell` resolves from a layout effect on the next location commit, with a time limit so a slow lazy chunk
 * can never leave the page frozen under the transition.
 */

type DocumentWithTransition = Document & { startViewTransition?: (callback: () => Promise<void> | void) => unknown }

const LIMIT_MS = 1500
const waiting = new Set<() => void>()

export function canViewTransition(): boolean {
  return typeof document !== 'undefined'
    && motionOn()
    && typeof (document as DocumentWithTransition).startViewTransition === 'function'
}

/**
 * The three route moves (APP-DESIGN-DIRECTION 3.9): `lateral` between top-level destinations (a short rise), `forward` when
 * drilling in (the new page slides from the reading end), `back` the mirror. The type is a class on <html> for the length of
 * the transition, read by the `::view-transition-*` rules in r5-motion.css.
 */
export type RouteMove = 'lateral' | 'forward' | 'back'
const MOVE_CLASSES = ['r5-vt-lateral', 'r5-vt-forward', 'r5-vt-back']

/** Infers the move from the two paths: deeper is forward, shallower is back, a sibling or a new top level is lateral. */
export function routeMove(from: string, to: string): RouteMove {
  const depth = (path: string) => path.split('/').filter(Boolean).length
  const a = depth(from)
  const b = depth(to)
  if (b > a && to.startsWith(from.replace(/\/$/, '') + '/')) return 'forward'
  if (b > a) return 'forward'
  if (b < a) return 'back'
  return 'lateral'
}

type ViewTransitionLike = { finished?: Promise<unknown> }

/** Run `navigate` inside a view transition. Falls back to a plain call when transitions are unavailable. */
export function navigateWithTransition(navigate: () => void, move: RouteMove = 'lateral') {
  if (!canViewTransition()) {
    navigate()
    return
  }
  const root = document.documentElement
  root.classList.remove(...MOVE_CLASSES)
  root.classList.add(`r5-vt-${move}`)
  const transition = (document as DocumentWithTransition).startViewTransition!(() => {
    const committed = afterRouteCommit()
    navigate()
    return committed
  }) as ViewTransitionLike | undefined
  const clear = () => root.classList.remove(`r5-vt-${move}`)
  if (transition?.finished) transition.finished.then(clear, clear)
  else window.setTimeout(clear, LIMIT_MS)
}

/** Resolves on the next location commit, or after a time limit, so nothing can wait on a navigation that never lands. */
export function afterRouteCommit(): Promise<void> {
  return new Promise<void>((resolve) => {
    const done = () => {
      waiting.delete(done)
      window.clearTimeout(timer)
      resolve()
    }
    const timer = window.setTimeout(done, LIMIT_MS)
    waiting.add(done)
  })
}

/** Called by `R5Shell` (layout effect) whenever the location commits. Lets a waiting transition capture the new page. */
export function notifyRouteCommitted() {
  for (const done of [...waiting]) done()
}
